import React, { useState, useEffect, useCallback } from 'react';
import { theme } from '../styles/theme';
import { createCampaign, fetchAudience, fetchDefaultLogo, fetchDeviceStats, uploadNotificationImage } from '../services/pushApi';
import type {
  AudienceDevice,
  AudienceResponse,
  AudienceSummary,
  AudienceUser,
  CreateCampaignResult,
  DeviceStats,
  PushAudience,
  PushCampaign,
  PushOptionsValue,
  SegmentFilters as SegmentFiltersValue,
} from '../types/push';
import { findPushScreen } from '../config/pushScreens';
import { findPushChannel } from '../config/pushChannels';
import { DEFAULT_PUSH_OPTIONS, customDataError, customDataToObject } from '../utils/pushOptions';
import { formatDateTime } from '../services/pushMetrics';
import { isBelowVersion } from '../utils/version';
import PushOptions from './push/PushOptions';
import PushPreview from './push/PushPreview';
import PushHistory from './push/PushHistory';
import SegmentFilters, { EMPTY_SEGMENT } from './push/SegmentFilters';
import PushCampaignDetail from './push/PushCampaignDetail';
import HistoryIcon from '@mui/icons-material/History';
import ConfirmSendDialog, { ConfirmSendInfo } from './push/ConfirmSendDialog';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MessageIcon from '@mui/icons-material/Message';
import SendIcon from '@mui/icons-material/Send';
import PeopleIcon from '@mui/icons-material/People';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase';
import SearchIcon from '@mui/icons-material/Search';

/** Limite do "Enviar teste": é para mandar para você mesmo, não para uma turma. */
const MAX_TEST_USERS = 5;

/** Janela do agendamento (a API confere de novo, com uma pequena folga). */
const SCHEDULE_MIN_AHEAD_MS = 5 * 60 * 1000;
const SCHEDULE_MAX_AHEAD_MS = 30 * 24 * 60 * 60 * 1000;

/** Date → "AAAA-MM-DDTHH:mm" na hora local, formato do <input type="datetime-local">. */
const toLocalInputValue = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Minúsculas e sem acento, para a busca achar "João" digitando "joao". */
const normalizeText = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const formatDay = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');

const daysSince = (iso: string | null) =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000)) : null;

/** Texto do alerta depois do envio: quem recebeu e por que os demais não. */
const formatSendResult = (result: CreateCampaignResult) => {
  if (result.status === 'scheduled') {
    return [
      `📅 Notificação agendada para ${formatDateTime(new Date(result.scheduledAt))}.`,
      '',
      'O público é calculado na hora do envio (até 5 minutos depois do horário).',
      'Dá para cancelar pelo Histórico enquanto ela não sair.',
    ].join('\n');
  }
  const { stats } = result;
  const lines = [
    result.status === 'sent' ? '✅ Notificação enviada!' : '❌ Nenhuma notificação foi aceita pelo FCM',
    '',
    `Aparelhos no público: ${stats.targeted} (${stats.users} ${stats.users === 1 ? 'negócio' : 'negócios'})`,
    `Enviadas: ${stats.sent}`,
  ];
  if (stats.invalidToken) {
    const marked = stats.invalidToken === 1 ? 'agora marcado como inativo' : 'agora marcados como inativos';
    lines.push(`App desinstalado ou token expirado: ${stats.invalidToken} (${marked})`);
  }
  if (stats.skippedInvalid) lines.push(`Já inativos, não enviados: ${stats.skippedInvalid}`);
  if (stats.failed) lines.push(`Falhas: ${stats.failed}`);
  if (result.warnings.length) lines.push('', ...result.warnings.map((warning) => `⚠️ ${warning}`));
  return lines.join('\n');
};

const Messages: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'notification' | 'history' | 'message'>('notification');
  const [openCampaignId, setOpenCampaignId] = useState<string | null>(null);
  const [users, setUsers] = useState<AudienceUser[]>([]);
  const [summary, setSummary] = useState<AudienceSummary | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [defaultLogo, setDefaultLogo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState<DeviceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [segment, setSegment] = useState<SegmentFiltersValue>(EMPTY_SEGMENT);
  const [audienceLabel, setAudienceLabel] = useState('Todos');
  const [appVersions, setAppVersions] = useState<AudienceResponse['appVersions']>([]);
  const [targetVersions, setTargetVersions] = useState<AudienceResponse['targetAppVersions']>([]);
  const [options, setOptions] = useState<PushOptionsValue>(DEFAULT_PUSH_OPTIONS);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledFor, setScheduledFor] = useState(''); // valor do <input type="datetime-local">, hora local
  const [confirm, setConfirm] = useState<(ConfirmSendInfo & { audience: PushAudience }) | null>(null);

  const hasSegmentFilter = Object.values(segment).some(value => value !== null);

  // A lista e a contagem vêm do servidor, com a mesma regra de segmento do envio.
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAudience({ type: 'segment', ...segment });
      setUsers(data.users);
      setSummary(data.summary);
      setAudienceLabel(data.label);
      setAppVersions(data.appVersions);
      setTargetVersions(data.targetAppVersions);
    } catch (error) {
      console.error('❌ Erro ao carregar usuários:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      alert('Erro ao carregar usuários: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  }, [segment]);

  const loadStats = async () => {
    try {
      setStats(await fetchDeviceStats());
    } catch (error) {
      console.error('❌ Erro ao carregar estatísticas:', error);
    }
  };

  useEffect(() => {
    loadStats();
    loadDefaultLogo();
  }, []);

  // A lista vem do servidor já filtrada pelo segmento (mesma regra do envio).
  useEffect(() => {
    loadUsers();
    setSelectedUsers([]);
  }, [loadUsers]);

  const loadDefaultLogo = async () => {
    try {
      const logoUrl = await fetchDefaultLogo();
      setDefaultLogo(logoUrl);
      console.log('🎨 Logo padrão carregada:', logoUrl);
    } catch (error) {
      console.error('❌ Erro ao carregar logo padrão:', error);
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    // Formatos que o Android mostra na notificação (a API confere de novo).
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      alert('Use uma imagem PNG, JPEG ou WEBP');
      return;
    }

    // Limite do FCM para imagem de notificação no Android: acima de 1MB ela não aparece.
    if (file.size > 1024 * 1024) {
      alert('Imagem muito grande. Máximo: 1MB (limite do Android para notificações)');
      return;
    }

    setImageFile(file);
    
    // Preview local
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Busca só filtra a lista exibida; o "Enviar para Todos" continua sendo o segmento inteiro.
  const query = normalizeText(search.trim());
  const matchesSearch = (user: AudienceUser) =>
    !query ||
    [user.name, user.email, ...user.devices.map(device => device.label)].some(field =>
      normalizeText(field || '').includes(query)
    );

  // Negócios sem nenhum aparelho ativo só aparecem com "Mostrar inativos" ou numa busca.
  const filteredUsers = users.filter(user => (showInactive || user.reachable || query) && matchesSearch(user));

  const visibleSelectable = filteredUsers.filter(u => u.reachable).map(u => u.userId);
  const allVisibleSelected =
    visibleSelectable.length > 0 && visibleSelectable.every(id => selectedUsers.includes(id));

  // Marca/desmarca só os visíveis; quem já estava marcado fora da busca continua marcado.
  const handleSelectAll = () => {
    setSelectedUsers(prev =>
      allVisibleSelected
        ? prev.filter(id => !visibleSelectable.includes(id))
        : Array.from(new Set([...prev, ...visibleSelectable]))
    );
  };

  /**
   * Arquivo escolhido → upload; senão a URL já informada (ex.: campanha
   * duplicada); senão sem imagem — a notificação fica só com o ícone do app.
   */
  const resolveImageUrl = async (): Promise<string | null> => {
    if (imageFile) {
      return uploadNotificationImage(imageFile);
    }
    return imageUrl.trim() || null;
  };

  const sendCampaign = async (audience: PushAudience, isTest = false) => {
    setSending(true);
    setUploading(true);

    try {
      const finalImageUrl = await resolveImageUrl();
      setUploading(false);

      const result = await createCampaign({
        name: options.name,
        title,
        body,
        imageUrl: finalImageUrl,
        screen: options.screen || null,
        customData: customDataToObject(options.customData),
        sound: options.sound,
        androidChannelId: options.androidChannelId,
        ttlSeconds: options.ttlSeconds,
        audience,
        // Teste sai sempre na hora.
        scheduledAt: !isTest && scheduleMode ? new Date(scheduledFor).toISOString() : null,
        isTest,
      });
      alert(formatSendResult(result));

      // Depois de um teste o conteúdo fica, para ajustar e enviar de verdade.
      if (!isTest) {
        setTitle('');
        setBody('');
        setImageUrl('');
        setImageFile(null);
        setOptions(DEFAULT_PUSH_OPTIONS);
        setScheduleMode(false);
      }
      setSelectedUsers([]);
      // Tokens mortos acabaram de ser marcados: recarrega selos e contadores.
      loadUsers();
      loadStats();
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      alert('Erro ao enviar notificação: ' + errorMessage);
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  /** Problema no formulário que impede o envio, ou null. O teste ignora o agendamento. */
  const formError = (isTest = false): string | null => {
    if (!title.trim() || !body.trim()) return 'Preencha título e mensagem';
    if (!isTest && scheduleMode) {
      const time = new Date(scheduledFor).getTime();
      if (!scheduledFor || Number.isNaN(time)) return 'Escolha a data e a hora do agendamento';
      if (time < Date.now() + SCHEDULE_MIN_AHEAD_MS) return 'Agende para pelo menos 5 minutos à frente';
      if (time > Date.now() + SCHEDULE_MAX_AHEAD_MS) return 'Agende para no máximo 30 dias à frente';
    }
    return customDataError(options.customData);
  };

  /** Liga o agendamento sugerindo daqui a 1 hora, arredondado para os próximos 15 minutos. */
  const enableSchedule = () => {
    const quarter = 15 * 60 * 1000;
    setScheduledFor(toLocalInputValue(new Date(Math.ceil((Date.now() + 60 * 60 * 1000) / quarter) * quarter)));
    setScheduleMode(true);
  };

  /**
   * Avisos de versão: telas e canais novos só funcionam a partir de certa versão
   * do app. Aparelho com versão desconhecida (app que ainda não reporta) conta
   * como anterior.
   */
  const versionWarnings = (deviceVersions: (string | null)[]): string[] => {
    const screen = findPushScreen(options.screen);
    const channel = findPushChannel(options.androidChannelId);
    const requirements = [
      screen?.minAppVersion && { version: screen.minAppVersion, effect: `não vai para "${screen.label}", só abre o app` },
      channel?.minAppVersion && { version: channel.minAppVersion, effect: 'pode tocar som no Android' },
    ].filter((item): item is { version: string; effect: string } => !!item);

    return requirements.flatMap(({ version, effect }) => {
      const outdated = deviceVersions.filter(deviceVersion => isBelowVersion(deviceVersion, version)).length;
      if (outdated === 0) return [];
      return [`${outdated} de ${deviceVersions.length} aparelhos estão em versão anterior à ${version} (ou desconhecida): para eles a notificação ${effect}.`];
    });
  };

  const handleSendNotification = () => {
    const error = formError();
    if (error) {
      alert(error);
      return;
    }

    if (selectedUsers.length === 0) {
      alert('Selecione pelo menos um usuário');
      return;
    }

    const selected = users.filter(user => selectedUsers.includes(user.userId));
    const activeDevices = selected.flatMap(user => user.devices.filter(device => device.status === 'active'));
    setConfirm({
      audience: { type: 'users', userIds: selectedUsers },
      audienceLabel: selectedUsers.length === 1 ? '1 selecionado' : `${selectedUsers.length} selecionados`,
      users: selected.filter(user => user.reachable).length,
      devices: activeDevices.length,
      warnings: versionWarnings(activeDevices.map(device => device.appVersion)),
    });
  };

  const handleSendToAll = () => {
    const error = formError();
    if (error) {
      alert(error);
      return;
    }

    // Expande a contagem por versão em uma lista de versões, uma por aparelho.
    const deviceVersions = targetVersions.flatMap(({ version, devices }) => Array<string | null>(devices).fill(version));
    setConfirm({
      audience: { type: 'segment', ...segment },
      audienceLabel,
      users: summary?.users ?? 0,
      devices: summary?.devices ?? 0,
      warnings: versionWarnings(deviceVersions),
    });
  };

  /** Teste vai direto, sem confirmação, para no máximo MAX_TEST_USERS negócios. */
  const handleSendTest = async () => {
    const error = formError(true);
    if (error) {
      alert(error);
      return;
    }
    if (selectedUsers.length === 0 || selectedUsers.length > MAX_TEST_USERS) {
      alert(`Para o teste, selecione de 1 a ${MAX_TEST_USERS} usuários (ex.: a sua conta)`);
      return;
    }
    await sendCampaign({ type: 'users', userIds: selectedUsers }, true);
  };

  /** "Duplicar" do histórico: copia conteúdo e opções para o formulário; o público é escolhido de novo. */
  const handleDuplicate = (campaign: PushCampaign) => {
    setTitle(campaign.title);
    setBody(campaign.body);
    setImageFile(null);
    setImageUrl(campaign.imageUrl || '');
    setOptions({
      name: campaign.name === campaign.title ? '' : campaign.name,
      screen: campaign.screen || '',
      sound: campaign.sound,
      androidChannelId: campaign.androidChannelId || DEFAULT_PUSH_OPTIONS.androidChannelId,
      ttlSeconds: campaign.ttlSeconds || DEFAULT_PUSH_OPTIONS.ttlSeconds,
      customData: Object.entries(campaign.customData).map(([key, value]) => ({ key, value })),
    });
    setOpenCampaignId(null);
    setActiveTab('notification');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleConfirmSend = async () => {
    if (!confirm) return;
    const { audience } = confirm;
    setConfirm(null);
    await sendCampaign(audience);
  };

  /** Selo de um aparelho na lista: ativo, inativo (token morto) ou sem abrir o app há tempos. */
  const renderDeviceBadge = (device: AudienceDevice, idx: number) => {
    const inactive = device.status === 'inactive';
    // Só o app 1.12+ informa a permissão; antes disso ela é desconhecida.
    const denied = !inactive && device.permission === 'denied';
    const idleDays = daysSince(device.lastUpdate);
    const badgeStyle = inactive
      ? styles.deviceBadgeInactive
      : denied || device.stale
        ? styles.deviceBadgeStale
        : styles.deviceBadge;
    const version = device.appVersion ? `App ${device.appVersion}` : 'Versão do app desconhecida (anterior à 1.12)';
    const hint = inactive
      ? `Inativo desde ${formatDay(device.invalidAt)}: app desinstalado ou token expirado. Volta a ficar ativo quando o lojista abrir o app.`
      : denied
        ? `Notificações desativadas no aparelho: o envio é aceito, mas não aparece para o lojista. ${version}.`
        : device.stale
          ? `Sem abrir o app há ${idleDays} dias (ainda recebe). ${version}.`
          : `Último acesso: ${formatDay(device.lastUpdate)}. ${version}.`;

    return (
      <span key={idx} style={badgeStyle} title={hint}>
        {inactive ? (
          <PhonelinkEraseIcon style={{ fontSize: 14, marginRight: 4 }} />
        ) : device.platform === 'android' ? (
          <AndroidIcon style={{ fontSize: 14, marginRight: 4 }} />
        ) : device.platform === 'ios' ? (
          <AppleIcon style={{ fontSize: 14, marginRight: 4 }} />
        ) : null}
        {device.label}
        {device.appVersion && <span style={styles.versionTag}>v{device.appVersion}</span>}
        {inactive && ' · inativo'}
        {denied && ' · notificações desativadas'}
        {!inactive && !denied && device.stale && ` · ${idleDays}d sem abrir`}
      </span>
    );
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Central de Notificações</h1>
        <button style={styles.refreshButton} onClick={() => { loadUsers(); loadStats(); }} title="Atualizar">
          <RefreshIcon style={{ fontSize: 24 }} />
        </button>
      </header>

      {/* Estatísticas */}
      {stats && (
        <div style={styles.statsContainer}>
          <div style={styles.statCard}>
            <PeopleIcon style={{ fontSize: 32, color: theme.colors.primary }} />
            <div>
              <div style={styles.statNumber}>{stats.reachableUsers}</div>
              <div style={styles.statLabel}>Negócios alcançáveis</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <NotificationsIcon style={{ fontSize: 32, color: '#4CAF50' }} />
            <div>
              <div style={styles.statNumber}>{stats.activeDevices}</div>
              <div style={styles.statLabel}>Aparelhos ativos</div>
            </div>
          </div>
          <div style={styles.statCard} title="App desinstalado ou token expirado. Não recebem até o lojista abrir o app de novo.">
            <PhonelinkEraseIcon style={{ fontSize: 32, color: '#9e9e9e' }} />
            <div>
              <div style={styles.statNumber}>{stats.inactiveDevices}</div>
              <div style={styles.statLabel}>Inativos</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <AndroidIcon style={{ fontSize: 32, color: '#3DDC84' }} />
            <div>
              <div style={styles.statNumber}>{stats.android}</div>
              <div style={styles.statLabel}>Android</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <AppleIcon style={{ fontSize: 32, color: '#000' }} />
            <div>
              <div style={styles.statNumber}>{stats.ios}</div>
              <div style={styles.statLabel}>iOS</div>
            </div>
          </div>
        </div>
      )}

      <div style={styles.content}>
        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'notification' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('notification')}
          >
            <NotificationsIcon style={{ marginRight: 8 }} />
            Notificações Push
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'history' ? styles.tabActive : {})
            }}
            onClick={() => {
              setActiveTab('history');
              setOpenCampaignId(null);
            }}
          >
            <HistoryIcon style={{ marginRight: 8 }} />
            Histórico
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'message' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('message')}
          >
            <MessageIcon style={{ marginRight: 8 }} />
            Mensagens (Em breve)
          </button>
        </div>

        {activeTab === 'notification' && (
          <div style={styles.tabContent}>
            {/* Formulário de envio */}
            <div style={styles.formCard}>
              <h2 style={styles.cardTitle}>Enviar Notificação</h2>

              <div style={styles.composerLayout}>
              <div style={styles.composerFields}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Título da Notificação</label>
                <input
                  style={styles.input}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Nova atualização disponível"
                  maxLength={100}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Mensagem</label>
                <textarea
                  style={styles.textarea}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Ex: Confira as novidades da versão 2.0"
                  maxLength={500}
                  rows={4}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Imagem <span style={{color: '#999', fontSize: '12px'}}>- Opcional (PNG, JPEG ou WEBP até 1MB). Sem imagem, a notificação mostra só o ícone do app</span>
                </label>
                <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleImageSelect}
                    style={{display: 'none'}}
                    id="image-upload"
                  />
                  <label 
                    htmlFor="image-upload" 
                    style={{
                      ...styles.uploadButton,
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      opacity: uploading ? 0.6 : 1
                    }}
                  >
                    <CloudUploadIcon style={{ marginRight: 8, fontSize: 20 }} />
                    {uploading ? 'Fazendo upload...' : (imageFile ? imageFile.name : 'Escolher imagem do PC')}
                  </label>
                  {(imageFile || imageUrl) && (
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        setImageUrl('');
                      }}
                      style={{
                        padding: '8px 12px',
                        background: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Remover
                    </button>
                  )}
                </div>
                {imageUrl && (
                  <div style={{marginTop: '10px'}}>
                    <img 
                      src={imageUrl} 
                      alt="Preview" 
                      style={{
                        maxWidth: '200px', 
                        maxHeight: '100px', 
                        borderRadius: '8px', 
                        objectFit: 'cover',
                        border: '2px solid #e0e0e0'
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <PushOptions value={options} onChange={setOptions} disabled={sending} />

              <div style={styles.formGroup}>
                <label style={styles.label}>Quando enviar</label>
                <div style={styles.scheduleRow}>
                  <label style={styles.radioLabel}>
                    <input type="radio" checked={!scheduleMode} onChange={() => setScheduleMode(false)} disabled={sending} />
                    Agora
                  </label>
                  <label style={styles.radioLabel}>
                    <input type="radio" checked={scheduleMode} onChange={enableSchedule} disabled={sending} />
                    Agendar
                  </label>
                  {scheduleMode && (
                    <input
                      type="datetime-local"
                      style={styles.dateInput}
                      value={scheduledFor}
                      min={toLocalInputValue(new Date(Date.now() + SCHEDULE_MIN_AHEAD_MS))}
                      max={toLocalInputValue(new Date(Date.now() + SCHEDULE_MAX_AHEAD_MS))}
                      onChange={(e) => setScheduledFor(e.target.value)}
                      disabled={sending}
                    />
                  )}
                </div>
                {scheduleMode && (
                  <div style={styles.scheduleHint}>
                    Sai em até 5 minutos depois do horário (horário de Brasília). O público é calculado na hora do envio, e
                    dá para cancelar pelo Histórico. O "Enviar teste" continua saindo na hora.
                  </div>
                )}
              </div>
              </div>

              <div style={styles.previewColumn}>
                <PushPreview title={title} body={body} imageUrl={imageUrl || null} appIcon={defaultLogo} />
              </div>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  style={styles.sendButton}
                  onClick={handleSendNotification}
                  disabled={sending || selectedUsers.length === 0}
                >
                  <SendIcon style={{ marginRight: 8 }} />
                  {sending
                    ? 'Enviando...'
                    : `${scheduleMode ? 'Agendar' : 'Enviar'} para Selecionados (${selectedUsers.length})`}
                </button>
                <button
                  style={styles.testButton}
                  onClick={handleSendTest}
                  disabled={sending || selectedUsers.length === 0 || selectedUsers.length > MAX_TEST_USERS}
                  title={`Envia sem confirmação para até ${MAX_TEST_USERS} selecionados e marca como teste no histórico`}
                >
                  Enviar teste
                </button>
                <button
                  style={styles.sendAllButton}
                  onClick={handleSendToAll}
                  disabled={sending || !summary?.devices}
                  title={summary?.inactiveSkipped ? `${summary.inactiveSkipped} aparelhos inativos serão pulados` : undefined}
                >
                  {scheduleMode ? 'Agendar' : 'Enviar'} {hasSegmentFilter ? 'para o segmento' : 'para Todos'} ({summary?.users ?? 0} · {summary?.devices ?? 0} aparelhos)
                </button>
              </div>
            </div>

            {/* Segmento (define o "Enviar para todos") e busca (só filtra a lista) */}
            <div style={{ ...styles.filterContainer, flexDirection: 'column', alignItems: 'stretch' }}>
              <SegmentFilters value={segment} onChange={setSegment} appVersions={appVersions} />
              <div style={styles.searchRow}>
              <div style={styles.searchBox}>
                <SearchIcon style={{ fontSize: 20, color: '#999' }} />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nome, e-mail ou aparelho"
                  style={styles.searchInput}
                />
              </div>
              <label style={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  style={styles.checkbox}
                />
                Mostrar inativos
              </label>
              </div>
            </div>

            {/* Lista de usuários */}
            <div style={styles.usersCard}>
              <div style={styles.usersHeader}>
                <h2 style={styles.cardTitle}>
                  Usuários ({query ? `${filteredUsers.length} de ${users.length}` : filteredUsers.length})
                </h2>
                <button
                  style={styles.selectAllButton}
                  onClick={handleSelectAll}
                  disabled={visibleSelectable.length === 0}
                >
                  {allVisibleSelected
                    ? (query ? 'Desmarcar encontrados' : 'Desmarcar Todos')
                    : (query ? 'Selecionar encontrados' : 'Selecionar Todos')}
                </button>
              </div>

              {loading ? (
                <div style={styles.loading}>Carregando...</div>
              ) : filteredUsers.length === 0 ? (
                <div style={styles.emptyState}>
                  <NotificationsIcon style={styles.emptyIcon} />
                  <p>
                    {query
                      ? `Nenhum usuário encontrado para "${search.trim()}"`
                      : 'Nenhum usuário com aparelho ativo encontrado'}
                  </p>
                </div>
              ) : (
                <div style={styles.usersList}>
                  {filteredUsers.map(user => (
                    <div key={user.userId} style={user.reachable ? styles.userCard : { ...styles.userCard, opacity: 0.6 }}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.userId)}
                        onChange={() => handleSelectUser(user.userId)}
                        disabled={!user.reachable}
                        title={user.reachable ? undefined : 'Nenhum aparelho ativo'}
                        style={styles.checkbox}
                      />
                      <div style={styles.userInfo}>
                        <div style={styles.userName}>{user.name}</div>
                        <div style={styles.userEmail}>{user.email}</div>
                        <div style={styles.userDevices}>
                          {user.devices.map(renderDeviceBadge)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div style={styles.tabContent}>
            {openCampaignId ? (
              <PushCampaignDetail
                campaignId={openCampaignId}
                onBack={() => setOpenCampaignId(null)}
                onDuplicate={handleDuplicate}
              />
            ) : (
              <PushHistory onOpen={(campaign) => setOpenCampaignId(campaign.id)} />
            )}
          </div>
        )}

        {activeTab === 'message' && (
          <div style={styles.tabContent}>
            <div style={styles.emptyState}>
              <MessageIcon style={styles.emptyIcon} />
              <p style={styles.emptyText}>Funcionalidade de mensagens em desenvolvimento</p>
            </div>
          </div>
        )}
      </div>

      <ConfirmSendDialog
        info={confirm}
        title={title}
        body={body}
        hasImage={!!imageFile || !!imageUrl.trim()}
        options={options}
        scheduledAt={scheduleMode && scheduledFor ? new Date(scheduledFor) : null}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirmSend}
      />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
  },
  header: {
    background: theme.colors.white,
    padding: '25px 40px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    color: theme.colors.primary,
    margin: 0,
    fontSize: '28px',
    fontWeight: 'bold',
  },
  refreshButton: {
    padding: '12px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.primary}`,
    background: 'transparent',
    color: theme.colors.primary,
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s',
    minWidth: '48px',
    minHeight: '48px',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    padding: '20px 40px',
  },
  statCard: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  statNumber: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
  statLabel: {
    fontSize: '14px',
    color: theme.colors.secondary,
  },
  content: {
    padding: '20px 40px 40px',
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: `2px solid #e0e0e0`,
  },
  tab: {
    padding: '15px 30px',
    border: 'none',
    background: 'transparent',
    color: theme.colors.secondary,
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '3px solid transparent',
    transition: 'all 0.3s',
  },
  tabActive: {
    color: theme.colors.primary,
    borderBottom: `3px solid ${theme.colors.primary}`,
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formCard: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: theme.colors.primary,
    marginBottom: '20px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border 0.3s',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border 0.3s',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  buttonGroup: {
    display: 'flex',
    gap: '15px',
    marginTop: '20px',
  },
  uploadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '12px 20px',
    borderRadius: '8px',
    border: `2px dashed ${theme.colors.primary}`,
    background: 'white',
    color: theme.colors.primary,
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.3s',
  },
  sendButton: {
    flex: 1,
    padding: '15px 30px',
    borderRadius: '8px',
    border: 'none',
    background: theme.gradient,
    color: 'white',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s',
  },
  sendAllButton: {
    padding: '15px 30px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.primary}`,
    background: 'white',
    color: theme.colors.primary,
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  scheduleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '20px',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '15px',
    color: theme.colors.text,
    cursor: 'pointer',
  },
  dateInput: {
    padding: '10px 12px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
  },
  scheduleHint: {
    marginTop: '10px',
    fontSize: '13px',
    color: '#888',
  },
  testButton: {
    padding: '15px 20px',
    borderRadius: '8px',
    border: '2px dashed #9e9e9e',
    background: 'white',
    color: '#616161',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  composerLayout: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '30px',
    alignItems: 'flex-start',
  },
  composerFields: {
    flex: '1 1 480px',
    minWidth: 0,
  },
  previewColumn: {
    flex: '0 1 320px',
    position: 'sticky',
    top: '20px',
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '15px',
    background: 'white',
    padding: '15px 30px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  select: {
    padding: '10px 15px',
    fontSize: '14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
  },
  usersCard: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  usersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '15px',
  },
  searchBox: {
    flex: 1,
    minWidth: '220px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    background: 'white',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    background: 'transparent',
  },
  selectAllButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.primary}`,
    background: 'transparent',
    color: theme.colors.primary,
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '18px',
    color: theme.colors.secondary,
  },
  emptyState: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    padding: '40px',
  },
  emptyIcon: {
    fontSize: '80px',
    opacity: 0.5,
    color: theme.colors.primary,
  },
  emptyText: {
    fontSize: '18px',
    color: theme.colors.text,
    maxWidth: '400px',
  },
  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  userCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '15px',
    padding: '20px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    transition: 'all 0.3s',
    cursor: 'pointer',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    marginTop: '5px',
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: theme.colors.primary,
    marginBottom: '5px',
  },
  userEmail: {
    fontSize: '14px',
    color: theme.colors.secondary,
    marginBottom: '10px',
  },
  userDevices: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  deviceBadge: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 12px',
    background: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  versionTag: {
    marginLeft: '6px',
    padding: '1px 6px',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.7)',
    fontSize: '11px',
    fontWeight: 'normal',
  },
  deviceBadgeInactive: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 12px',
    background: '#eeeeee',
    color: '#757575',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
    textDecoration: 'line-through',
  },
  deviceBadgeStale: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 12px',
    background: '#fff8e1',
    color: '#b26a00',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: theme.colors.text,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
};

export default Messages;
